/**
 * Step 2: Questions Preview - Review and edit generated questions
 */
import { useWizard, normalizeQuestion } from './WizardContext';
import GenerationLock from './GenerationLock';
import { ReadOnlyStepBanner, RegeneratedNotice } from './WizardBanners';
import RequirementContextCard from './RequirementContextCard';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArrowRight, ArrowLeft, Plus, Trash2, Lock } from 'lucide-react';
import { autoResizeTextarea } from '@/lib/autoResizeTextarea';
import { orderQuestionsForDisplay, hasStandardLabel } from '@/lib/questionDisplay';

const MANDATORY_FIELD_KEYS = [
  'ship_to_location',
  'need_by_date',
  'start_date',
  'quantity',
  'rfp_currency',
  'submission_deadline'
];

const StepQuestions = () => {
  const {
    loading,
    businessQuestions,
    vendorQuestions,
    setCurrentStep,
    handleProceedToAnswers,
    handleAddBusinessQuestion,
    handleEditBusinessQuestion,
    handleRemoveBusinessQuestion,
    isStepPreviouslyCompleted,
    isStepUnlocked,
    unlockStepForEdit,
    regeneratedNotice,
    procurementId,
    userRequirement,
    parsedRequirement,
    getProcurementType,
    boqItems,
  } = useWizard();

  const isReadOnly = isStepPreviouslyCompleted('questions') && !isStepUnlocked('questions');

  // ONE SHARED ORDER (Ashok ruling 2026-09-03): the same helper the Answers
  // step and the detail page render through, so a question's number is the
  // same on every surface. Display-only - businessQuestions is not
  // reordered; every handler below still receives the STORED index `i`.
  // MANDATORY_FIELD_KEYS above stays what it was (badge + delete lock) and
  // plays no part in the order - see CTRL-086's Known Limitation.
  const orderedQuestions = orderQuestionsForDisplay(
    businessQuestions,
    typeof getProcurementType === 'function' ? getProcurementType() : undefined,
    Boolean(boqItems && boqItems.length > 0),
  );

  if (isReadOnly) {
    return (
      <div className="relative">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-zinc-900 mb-2">
            Generated Questions
          </h2>
          <p className="text-zinc-600">
            Review and customize the questions. You can edit, add, or remove questions as needed.
          </p>
        </div>

        <ReadOnlyStepBanner stepId="questions" onEdit={() => unlockStepForEdit('questions', businessQuestions)} />

        <RequirementContextCard
          procurementId={procurementId}
          userRequirement={userRequirement}
          parsedRequirement={parsedRequirement}
        />

        <div className="grid lg:grid-cols-2 gap-6 w-full mb-8">
          <div className="bg-zinc-50 border border-zinc-200 p-4">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">
              Business Questions ({businessQuestions.length})
            </div>
            <div className="space-y-2">
              {orderedQuestions.map(({ index: i, number, label }) => (
                <div key={i} className="flex items-start gap-3 text-sm text-zinc-600" data-testid={`question-row-${i}`}>
                  <span className="text-zinc-400 font-bold">{number}.</span>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-zinc-50 border border-zinc-200 p-4">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">
              Vendor Questions ({vendorQuestions.length})
              <span className="font-normal normal-case ml-2 text-zinc-400">Editable in Review step</span>
            </div>
            <div className="space-y-2">
              {vendorQuestions.map((q, i) => (
                <div key={i} className="flex items-start gap-3 text-sm text-zinc-600">
                  <span className="text-zinc-400 font-bold">{i + 1}.</span>
                  <span>{q}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setCurrentStep(0)} className="border-zinc-200">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button
            onClick={handleProceedToAnswers}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-6"
            data-testid="continue-readonly-questions-btn"
          >
            <ArrowRight className="w-4 h-4 mr-2" /> Answer Questions
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <GenerationLock active={loading} />
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-zinc-900 mb-2">
          Generated Questions
        </h2>
        <p className="text-zinc-600">
          Review and customize the questions. You can edit, add, or remove questions as needed.
        </p>
      </div>

      {regeneratedNotice?.stepId === 'questions' && (
        <RegeneratedNotice message={regeneratedNotice.message} causeLabel={regeneratedNotice.causeLabel} />
      )}

      <RequirementContextCard
        procurementId={procurementId}
        userRequirement={userRequirement}
        parsedRequirement={parsedRequirement}
      />

      <div className="grid lg:grid-cols-2 gap-6 w-full mb-8">
        {/* Business Questions - Editable */}
        <div className="bg-white border border-zinc-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
              Business Questions ({businessQuestions.length})
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddBusinessQuestion}
              className="border-zinc-200 text-xs"
              data-testid="add-business-question-btn"
            >
              <Plus className="w-3 h-3 mr-1" /> Add Question
            </Button>
          </div>
          <div className="space-y-3">
            {orderedQuestions.map(({ index: i, number, label }) => {
              const q = businessQuestions[i];
              const nq = normalizeQuestion(q);
              const isMandatory = nq.is_mandatory || MANDATORY_FIELD_KEYS.includes(nq.field_key);
              // STANDARD LABEL (item 3): for the canonical mandatory keys the
              // agent's text is no longer rendered anywhere a buyer looks, so
              // an editor for it here would edit something invisible. Those
              // rows show the label as static text; quantity (the exception -
              // its text carries information) and every other question keep
              // the editable textarea.
              const standardLabelled = hasStandardLabel(nq.field_key);
              return (
              <div key={`bq-edit-${i}`} data-testid={`question-row-${i}`}>
                {isMandatory && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 mb-1 inline-block">
                    MANDATORY
                  </span>
                )}
                <div className="flex items-start gap-2">
                  <span className="text-[#0055FF] font-bold text-sm mt-2">{number}.</span>
                  <div className="flex-1">
                    {standardLabelled ? (
                      <p
                        className="text-sm text-zinc-800 font-medium py-2"
                        data-testid={`business-question-label-${i}`}
                      >
                        {label}
                      </p>
                    ) : (
                    <Textarea
                      ref={autoResizeTextarea}
                      value={nq.question}
                      onChange={(e) => {
                        handleEditBusinessQuestion(i, e.target.value);
                        autoResizeTextarea(e.target);
                      }}
                      placeholder="Enter your question..."
                      className="min-h-[60px] w-full text-sm bg-zinc-50 border-zinc-200 focus:border-[#0055FF] resize-none overflow-hidden"
                      data-testid={`business-question-input-${i}`}
                    />
                    )}
                  </div>
                  {isMandatory ? (
                    // Discoverability fix (2026-07-27): mandatory questions
                    // have no delete path at all (never a Button, never
                    // wired to a handler) - a plain non-interactive span,
                    // so it can't be tabbed to or activated. The native
                    // `title` shows the "cannot be removed" explanation on
                    // hover without a Radix Tooltip's portal/provider
                    // overhead for a single static string.
                    <span
                      className="inline-flex items-center justify-center h-8 w-8 text-zinc-300 opacity-40 hover:opacity-100 transition-opacity cursor-default"
                      title="Mandatory question — cannot be removed."
                      data-testid={`mandatory-question-lock-${i}`}
                    >
                      <Lock className="w-4 h-4" />
                    </span>
                  ) : (
                    // Discoverability fix (2026-07-27): the bin used to be
                    // opacity-0 (invisible until hover), so deletability
                    // wasn't discoverable at rest - now a faded watermark
                    // always, full opacity on hover. Delete behavior itself
                    // (handleRemoveBusinessQuestion) is unchanged.
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveBusinessQuestion(i)}
                      className="text-zinc-400 opacity-40 hover:opacity-100 hover:text-red-500 transition-opacity"
                      data-testid={`delete-business-question-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              );
            })}
            {businessQuestions.length === 0 && (
              <p className="text-zinc-400 text-sm italic">No questions yet. Click &quot;Add Question&quot; to create one.</p>
            )}
          </div>
        </div>
        
        {/* Vendor Questions - Read-only preview */}
        <div className="bg-zinc-50 border border-zinc-200 p-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">
            Vendor Questions ({vendorQuestions.length})
            <span className="font-normal normal-case ml-2 text-zinc-400">Editable in Review step</span>
          </div>
          <div className="space-y-2">
            {vendorQuestions.map((q, i) => (
              <div key={i} className="flex items-start gap-3 text-sm text-zinc-600">
                <span className="text-zinc-400 font-bold">{i + 1}.</span>
                <span>{q}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setCurrentStep(0)} disabled={loading} className="border-zinc-200">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button
          onClick={handleProceedToAnswers}
          disabled={loading || businessQuestions.filter(q => normalizeQuestion(q).question.trim()).length === 0}
          className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-6"
          data-testid="proceed-to-answers-btn"
        >
          <ArrowRight className="w-4 h-4 mr-2" /> Answer Questions
        </Button>
      </div>
    </div>
  );
};

export default StepQuestions;
